import React, { useEffect, useState } from "react";
import { api, asset } from "@/lib/api";
import { IMG } from "@/lib/api";
import { PageHeader } from "@/components/shared/SectionHeader";
import { Calendar, MapPin, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Events() {
  const [events, setEvents] = useState([]);
  const { user, login } = useAuth();

  useEffect(() => { api.get("/events").then(r => setEvents(r.data)); }, []);

  const rsvp = async (id) => {
    if (!user) { login(); return; }
    try { await api.post("/me/rsvp", { event_id: id }); toast.success("You're on the list."); }
    catch { toast.error("Something went wrong."); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Events"
        title="Live learning. Real conversations."
        description="Webinars, workshops, virtual classes, chapter meetups and our annual conference."
        image={IMG.grass}
      />

      <section className="container-x py-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-4">
          {events.map(e => {
            const d = new Date(e.date);
            return (
              <div key={e.id} data-testid={`event-${e.id}`} className="group rounded-lg border border-border overflow-hidden grid grid-cols-1 md:grid-cols-12 bg-card">
                <div className="md:col-span-4 relative aspect-[16/10] md:aspect-auto overflow-hidden">
                  <img src={asset(e.cover)} alt={e.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="md:col-span-8 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
                  <div className="text-center min-w-[72px]">
                    <div className="font-display text-4xl leading-none">{d.getDate()}</div>
                    <div className="eyebrow text-[10px] mt-1">{d.toLocaleString('en', { month: 'short' })} {d.getFullYear()}</div>
                  </div>
                  <div className="flex-1">
                    <div className="eyebrow text-[10px]">{e.kind}</div>
                    <div className="font-display text-2xl md:text-3xl mt-1">{e.title}</div>
                    <div className="mt-1 text-sm text-muted-foreground">Hosted by {e.host}</div>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {e.time}</span>
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {e.location}</span>
                    </div>
                    <p className="mt-4 text-sm">{e.description}</p>
                  </div>
                  <button onClick={() => rsvp(e.id)} className="btn-primary" data-testid={`event-rsvp-${e.id}`}>RSVP</button>
                </div>
              </div>
            );
          })}
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-28 rounded-lg border border-border bg-card p-6">
            <div className="eyebrow">This month</div>
            <div className="font-display text-4xl mt-2 leading-none">{new Date().toLocaleString('en', { month: 'long' })}</div>
            <div className="grid grid-cols-7 gap-1 mt-6 text-center">
              {["S","M","T","W","T","F","S"].map((d, i) => <div key={i} className="text-[10px] uppercase text-muted-foreground">{d}</div>)}
              {Array.from({ length: 35 }).map((_, i) => (
                <div key={i} className={"aspect-square rounded grid place-items-center text-xs " + ([4, 12, 19, 24].includes(i) ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground")}>
                  {i > 3 && i < 34 ? i - 3 : ""}
                </div>
              ))}
            </div>
            <div className="hairline my-6" />
            <div className="eyebrow">Annual Conference</div>
            <div className="font-display text-2xl mt-2">Missoula, MT</div>
            <div className="text-xs text-muted-foreground mt-1">Sep 19–21, 2026</div>
            <button className="btn-outline w-full mt-4">Reserve your spot</button>
          </div>
        </aside>
      </section>
    </div>
  );
}
